import { Controller } from "@hotwired/stimulus"
import { focusSearchForShortcut, buildHighlightRegex, splitTextForHighlight } from "../lib/activity_feed_search.mjs"

const HIGHLIGHT_CLASS = "activity-search-highlight"
// Tags whose text we never rewrap (form controls, code we shouldn't split, existing marks).
const SKIP_TAGS = new Set(["MARK", "SCRIPT", "STYLE", "INPUT", "TEXTAREA", "SELECT", "OPTION", "KBD"])

// Replaces the browser's page-only find shortcut while the claim activity feed is visible, and
// highlights the active search query in the rendered timeline (including lazily loaded pages).
export default class extends Controller {
  static targets = ["input"]

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    this.applyHighlights = this.applyHighlights.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
    // Older feed pages arrive via lazy Turbo frames appended inside this element; re-highlight them.
    this.element.addEventListener("turbo:frame-load", this.applyHighlights)
    this.applyHighlights()
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    this.element.removeEventListener("turbo:frame-load", this.applyHighlights)
  }

  handleKeydown(event) {
    focusSearchForShortcut(event, this.inputTarget)
  }

  itemsContainer() {
    return this.element.querySelector('[id^="claim-activity-feed-items-"]')
  }

  applyHighlights() {
    const container = this.itemsContainer()
    if (!container) return

    this.clearHighlights(container)

    const query = this.hasInputTarget ? this.inputTarget.value : ""
    const regex = buildHighlightRegex(query)
    if (!regex) return

    this.highlightTextNodes(container, query)
  }

  clearHighlights(container) {
    container.querySelectorAll(`mark.${HIGHLIGHT_CLASS}`).forEach((mark) => {
      const parent = mark.parentNode
      if (!parent) return
      while (mark.firstChild) parent.insertBefore(mark.firstChild, mark)
      parent.removeChild(mark)
      parent.normalize()
    })
  }

  highlightTextNodes(container, query) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT
        const parentTag = node.parentElement && node.parentElement.tagName
        if (parentTag && SKIP_TAGS.has(parentTag)) return NodeFilter.FILTER_REJECT
        return NodeFilter.FILTER_ACCEPT
      },
    })

    const textNodes = []
    let current
    while ((current = walker.nextNode())) textNodes.push(current)

    textNodes.forEach((node) => {
      const segments = splitTextForHighlight(node.nodeValue, query)
      if (!segments.some((segment) => segment.match)) return

      const fragment = document.createDocumentFragment()
      segments.forEach((segment) => {
        if (segment.match) {
          const mark = document.createElement("mark")
          mark.className = HIGHLIGHT_CLASS
          mark.textContent = segment.text
          fragment.appendChild(mark)
        } else {
          fragment.appendChild(document.createTextNode(segment.text))
        }
      })

      node.parentNode.replaceChild(fragment, node)
    })
  }
}
